<template>
  <div>
    <breadcrumbs home="Suppliers"/>

    <import-csv v-model="doImport" type="suppliers"
                :fields="[{name:'Name',mapTo:'',mandatory:true},
                          {name:'Address 1',mapTo:'',mandatory:false},
                          {name:'Address 2',mapTo:'',mandatory:false},
                          {name:'City',mapTo:'',mandatory:false},
                          {name:'Province',mapTo:'',mandatory:false},
                          {name:'Postal code',mapTo:'',mandatory:false},
                          {name:'Email',mapTo:'',mandatory:false},
                          {name:'Phone',mapTo:'',mandatory:false},
                          {name:'Currency',mapTo:'',mandatory:false},
                          {name:'Contact name',mapTo:'',mandatory:false},
                          {name:'Contact title',mapTo:'',mandatory:false},
                          {name:'Contact email',mapTo:'',mandatory:false},
                          {name:'Contact phone',mapTo:'',mandatory:false},
                          ]"
              importAction="vendorsImport"
              instructions='<ol class="mb-4">
                            <li>Name</li>
                            <li>Address 1 (optional)</li>
                            <li>Address 2 (optional)</li>
                            <li>City (optional)</li>
                            <li>Province (optional)</li>
                            <li>Postal code (optional)</li>
                            <li>Email (optional)</li>
                            <li>Phone (optional)</li>
                            <li>Currency (optional, default is CAD)</li>
                            <li>Contact name (optional)</li>
                            <li>Contact title (optional)</li>
                            <li>Contact email (optional)</li>
                            <li>Contact phone (optional)</li>
                            </ol>
                            <em>Note that not all information for a supplier can be imported.</em>
                            You should, therefore, check each imported supplier to ensure all settings are correct.'
              @refresh="$refs.table.buildList()"/>

    <purtrak-table name="suppliers" objectType="suppliers" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="vendors2" v-model="selected"
                   fields="id,name,active,suppliesInBond,currency,
                            email,phone,notes,
                            billingAddress {line1,line2,city,province,postalCode,country}"
                   :listFilters="listFilters"
                   :item-class="supplier => (!supplier.active ? 'grey--text ' : '')"
                   @dblclick="supplier => $router.push({name:'PurchasingInventoryReceipts',query:{supplierId:supplier.id,supplierName:supplier.name}})"
                   @itemExpanded="({item, value}) => expand(item, value)"
                   title="Suppliers"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[filters.status!='1'?(filters.status=='0'?'Inactive':'All'):'',
                                           filters.type=='2'?'':filters.type=='0'?'Does not supply alcohol':'Supplies alcohol']}"
                   download-endpoint="vendorsExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.search='';filters.status='1';filters.type='2'">
          <v-list-item>
            <filter-search label="Supplier name" v-model="filters.search"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{value:'2',text:'All'},{value:'1',text:'Active'},{value:'0',text:'Inactive'}]" v-model="filters.status"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Supplies alcohol" dense outlined :items="[{value:'2',text:'All'},{value:'1',text:'Yes'},{value:'0',text:'No'}]" v-model="filters.type"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">Supplier: {{filters.search}}</v-chip>
          <v-chip v-show="filters.status!='1'" close small class="ml-1" @click:close="filters.status='1'">{{filters.status=='0'?'Inactive':'All'}}</v-chip>
          <v-chip v-show="filters.type!='2'" close small class="ml-1" @click:close="filters.type='2'">{{filters.type=='0'?'Does not supply alcohol':'Supplies alcohol'}}</v-chip>
        </template>
      </template>
      <template v-slot:menu v-if="!readOnly">
        <v-list-item :to="{name:'PurchasingSuppliersEditNew'}"><v-list-item-title>Create supplier</v-list-item-title></v-list-item>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-divider/>
          <v-list-item @click="doImport=true"><v-list-item-title>Import suppliers from CSV</v-list-item-title></v-list-item>
          <v-list-item v-if="$store.getters.company.connectedToAccounting"@click="sync"><v-list-item-title>Sync suppliers with QBO</v-list-item-title></v-list-item>
        </template>
      </template>
      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <v-btn color="primary" outlined x-small style="position: absolute" class="mt-5 ml-10" @click="deleteMultiple=true;showDelete=true">Batch delete</v-btn>
      </template>
      <template v-slot:item.name="{item,printing}">
        <template v-if="printing">{{item.name}}</template>
        <template v-else>
          <div :style="nameStyle(item)">
            <v-hover v-if="!readOnly" v-slot="{hover}">
              <a @click.stop="$router.push({name:'PurchasingSuppliersEdit',params:{supplierId:item.id}})"
                 :class="{'text-decoration-underline':hover}">{{item.name}}</a>
            </v-hover>
            <template v-else>{{item.name}}</template>
          </div>
        </template>
      </template>
      <template v-slot:item.suppliesInBond="{value}">
        {{value?'Yes':''}}
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'PurchasingSuppliersEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}" v-if="!readOnly">
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Supplier operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'PurchasingInventoryReceipts',query:{supplierId:item.id,supplierName:item.name}}"><v-list-item-title>View inventory receipts</v-list-item-title></v-list-item>
            <v-list-item :to="{name:'PurchasingPurchaseOrders',query:{supplierId:item.id,supplierName:item.name}}"><v-list-item-title>View purchase orders</v-list-item-title></v-list-item>
            <v-list-item v-if="audit" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item :to="{name:'PurchasingSuppliersEdit',params:{supplierId:item.id}}"><v-list-item-title>Edit supplier</v-list-item-title></v-list-item>
              <v-list-item @click="supplier=item;deleteMultiple=false;showDelete=true"><v-list-item-title>Delete supplier</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <td :colspan="headers.length">
          <v-card class="my-4 text-caption" :class="expansionPanelClass">
          <v-container>
            <v-row>
              <v-col>
                <v-card flat :class="expansionPanelClass">
                  <div>{{item.email}}</div>
                  <div>{{item.phone}}</div>
                  <div v-if="item.notes">{{item.notes}}</div>
                </v-card>
              </v-col>
              <v-col>
                <v-card flat :class="expansionPanelClass">
                  <div class="font-weight-bold">Billing address</div>
                  <div>{{item.billingAddress.line1}}</div>
                  <div v-show="item.billingAddress.line2">{{item.billingAddress.line2}}</div>
                  <div>{{item.billingAddress.city}} {{item.billingAddress.province}} {{item.billingAddress.postalCode}}</div>
                </v-card>
              </v-col>
            </v-row>
            <v-row v-if="item.contacts && item.contacts.length">
              <v-col cols="9">
                <v-container>
                  <v-row dense>
                    <v-col cols="4"><strong>Contact</strong></v-col>
                    <v-col cols="2"><strong>Title</strong></v-col>
                    <v-col cols="4"><strong>Email</strong></v-col>
                    <v-col cols="2"><strong>Phone</strong></v-col>
                  </v-row>
                  <v-row v-for="contact in item.contacts" dense>
                    <v-col cols="4">{{contact.name}}</v-col>
                    <v-col cols="2">{{contact.title}}</v-col>
                    <v-col cols="4">{{contact.email}}</v-col>
                    <v-col cols="2">{{contact.phone}}</v-col>
                  </v-row>
                </v-container>
              </v-col>
            </v-row>
          </v-container>
          </v-card>
        </td>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="deleteMultiple?'ALL selected suppliers':supplier.name" @delete="performDelete(supplier.id)"/>
  </div>
</template>

<script>
import performDelete from '@/mixins/performDelete'

export default {
  name: 'PurchasingSuppliers',
  mixins: [performDelete],
  data() {
    return {
      filters: {
        status: '1',
        type:'2',
        search: '',
      },
      supplier: {},
      showDelete: false,
      deleteMultiple: false,
      doImport: false,
      defaultColumns: [{ value: 'name', visible:true},
                       { value: 'suppliesInBond', visible:true},
                       { value: 'currency', visible:false},
                       { value: 'email', visible:false},
                       { value: 'phone', visible:false}],
      headers: [{ text: 'NAME', value: 'name', width:'50%',sortable:true},
                { text: 'EMAIL', value: 'email' ,width:'20%',sortable:false},
                { text: 'PHONE', value: 'phone' ,width:'15%',sortable:false},
                { text: 'SUPPLIES ALCOHOL', value: 'suppliesInBond' ,width:'45%',align:'center',sortable:true},
                { text: 'CURRENCY', value: 'currency' ,width:'10%',align:'left'}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.company.permissions.includes('production.write');
    },
    audit() {
      return this.company.allowAudit && this.company.permissions.includes('audit');
    },
    listFilters() {
      const filters = [];
      if (this.filters.search)
        filters.push({field:'name', comparator:'contains', value:this.filters.search});
      if (this.filters.status != '2')
        filters.push({field:'active', comparator:'eq', value:this.filters.status === '1' ? 'true' : 'false'});
      if (this.filters.type != '2')
        filters.push({field:'suppliesInBond', comparator:'eq', value:this.filters.type=='1'?'true':'false'});
      return filters;
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');
  },
  methods: {
    async expand(item, value) {
      if (value && !item.contacts) {
        this.loading = true;
        const results = await this.$apiCall(`query PurchasingSuppliers($id:ID!) {
                                                  vendor(id:$id) {
                                                    contacts {
                                                      name,title,email,phone
                                                    }
                                                  }
                                                }`,
                                            {id:item.id});
        this.$set(item, 'contacts', results.data.vendor.contacts);
        this.loading = false;
      }
    },
    nameStyle(supplier) {
      return {
        color:supplier.active ? '' : '#ccc',
      }
    },
    async sync() {
      this.loading = true;
      const results = await this.$apiCall(`mutation PurchasingSuppliers {
                                             accountingIntegrationSync(step:"vendors") {name}
                                           }`, {});
      this.$refs.table?.buildList();
      if (!results.error)
        this.$store.commit('setError', {type:'success',message:`Synced suppliers with QBO`});
    }
  }
}
</script>